import Vue from 'vue'
import createComponent from './creator'
import { Dialog, Drawer } from 'element-ui'
import { formatOption } from './util'
import dialogState from './dialogState'

/**
 * 使用方式:
 * 预先写好模态框内容组件，比如
 * // page/xxx/components/dialog.vue
 * <div>{{name}}</div>
 *
 * 在使用的页面中 xxx.vue
 * import Tip from './components/dialog.vue'
 * ...
 * showUser(){
 *    this.$createDialog(Tip, { name: 'hcl' }, { title: '提示' }).show()
 * }
 *
 *
 */

/**
 * @param child 内容组件
 * @param option 传递给内容组件的事件或props，事件以 onChange 等形式，以on开头
 * @param dialogOption 传递给弹出框的配置和事件，事件名同上, 具体可配置项参见 element-ui https://element.eleme.cn/#/zh-CN/component/dialog
 * 默认情况下, 每次在弹窗关闭后销毁，可以配置 dialogOption.reuse 保存该次弹窗实例以复用
 * 例:
 * showData(data) {
 *  data = data || { name: 'hcl' }
 *  if (!this._dialog) {
 *    this._dialog = this.$createDialog(Tip, data, { title: '提示', reuse:true })
 *  }
 *  this._dialog.show(data)
 * }
 */
Vue.prototype.$createDialog = factory(Dialog)
Vue.prototype.$createDrawer = factory(Drawer)

function factory(container) {
  return function(child, option, dialogExtendOption = {}) {
    const {
      reuse = false,
      draggable = false,
      sceneType,
      defaultPosition, // 获取初始定位
      ...dialogOption
    } = dialogExtendOption

    let formatedChildOption = formatOption(option)
    const cachedChildOption = { ...formatedChildOption }
    // 用call绑定this主要是为了增强实例化vue组件【$route,$store,$router】 tips:当前this指向调用dialog的父组件实例
    let instance = createComponent.call(
      this,
      container,
      formatOption(dialogOption),
      h => h(child, formatedChildOption),
      {
        draggable,
        sceneType
      }
    )

    instance.$on('update:visible', $event => {
      instance.$updateProps({
        visible: $event
      })
    })

    instance.show = options => {
      if (draggable) {
        dialogState.register(sceneType, instance)
        // 赋值初始定位
        const position = options?.defaultPosition || defaultPosition
        dialogState.position(sceneType, instance, position)
      }
      formatedChildOption = options ? formatOption(options) : cachedChildOption
      instance.$emit('update:visible', true)
      if (sceneType) dialogState.update(sceneType, { visible: true }) // 更新打开状态
    }
    // 手动取消
    instance.hide = () => {
      instance?.$emit?.('update:visible', false)
      if (sceneType) dialogState.update(sceneType, { visible: false }) // 更新打开状态
    }

    instance.destroy = () => {
      instance && instance.$destroy()
      instance && instance.$nextTick(() => (instance = null))
    }
    instance.resize = () => {
      instance.$children?.[0]?.resizePosition?.()
    }

    const vm = this
    // 防止用户未释放，造成内存泄漏
    if (vm && vm._isVue && vm.$on && !draggable) {
      vm.$on('hook:beforeDestroy', () => {
        instance && instance.$destroy()
        instance && instance.$nextTick(() => (instance = null))
      })
    }

    if (!reuse) {
      instance.$on('closed', () => {
        instance && instance.$destroy()
        instance && instance.$nextTick(() => (instance = null))
      })
    }

    return instance
  }
}
